import React, { useState } from "react";
import { Box, Container, Typography } from "@mui/material";

// ── Helper sub-components ─────────────────────────────────────────────────────

const SectionHeading = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      mt: "20px",
      mb: "10px",
      "&::before": {
        content: '"▸"',
        color: "#c9962a",
        fontWeight: 700,
      },
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "14px",
        color: "#0d1b35",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const BodyText = ({ children }) => (
  <Typography
    sx={{ fontSize: "14px", color: "#6b6b80", lineHeight: 1.7, mb: "12px" }}
  >
    {children}
  </Typography>
);

const QuoteBlock = ({ children }) => (
  <Box
    sx={{
      background: "#fdfaf4",
      borderLeft: "3px solid #c9962a",
      borderRadius: "0 4px 4px 0",
      padding: "16px 20px",
      margin: "16px 0",
    }}
  >
    <Typography sx={{ fontSize: "14px", color: "#6b6b80", lineHeight: 1.7 }}>
      {children}
    </Typography>
  </Box>
);

const StatBlock = ({ number, label }) => (
  <Box
    sx={{
      background: "#fdfaf4",
      borderLeft: "3px solid #c9962a",
      borderRadius: "0 6px 6px 0",
      padding: "16px 20px",
      margin: "16px 0",
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "28px",
        fontWeight: 700,
        color: "#0d1b35",
      }}
    >
      {number}
    </Typography>
    <Typography sx={{ fontSize: "13px", color: "#6b6b80", mt: "4px" }}>
      {label}
    </Typography>
  </Box>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ listStyle: "none", m: "12px 0", p: 0 }}>
    {items.map((item, i) => (
      <Box
        component="li"
        key={i}
        sx={{
          paddingLeft: "24px",
          position: "relative",
          mb: "10px",
          fontSize: "14px",
          color: "#6b6b80",
          lineHeight: 1.6,
          "&::before": {
            content: '"✓"',
            position: "absolute",
            left: 0,
            color: "#c9962a",
            fontWeight: 700,
          },
        }}
      >
        {item}
      </Box>
    ))}
  </Box>
);

const CtaLink = ({ sectionId, children }) => {
  const handleClick = () => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "auto", block: "start" });
    }
  };

  return (
    <Box
      component="button"
      onClick={handleClick}
      sx={{
        display: "inline-block",
        mt: "16px",
        background: "#0d1b35",
        color: "#fff",
        padding: "11px 22px",
        borderRadius: "4px",
        textDecoration: "none",
        fontWeight: 600,
        fontSize: "13px",
        letterSpacing: "0.3px",
        border: "none",
        cursor: "pointer",
        transition: "background 0.2s",
        "&:hover": { background: "#1e2d52" },
      }}
    >
      {children}
    </Box>
  );
};

// ── Issues data ───────────────────────────────────────────────────────────────

const ISSUES = [
  {
    id: "education",
    icon: "🎓",
    title: "Education Accountability & Parental Rights",
    subtitle:
      "Appoquinimink does not have a funding problem; it has a results and leadership problem.",
    content: (
      <>
        <SectionHeading>The Problem</SectionHeading>
        <BodyText>
          In July 2025, the Appoquinimink School District raised taxes 10%
          without a public vote, using a legal loophole to bypass community
          input. This came after a $4.9M budget shortfall due to years of
          systemic accounting failures, as discovered by Delaware State Auditor,
          Lydia York, and documented in the news article by Spotlight Delaware.
        </BodyText>
        <QuoteBlock>
          According to the audit, this fiscal mismanagement occurred at several
          levels of school leadership, to include the school superintendent, the
          school chief financial officer, the school chief financial officer,
          the school board, and the appointed Financial Advisory Committee. As a
          result of the mismanagement, it triggered a 10% school tax increase.
          This is not a "snafu", or a "Pickle", nor the fault of just one
          person. It's systemic negligence and all should have been held
          accountable.
        </QuoteBlock>
        <BodyText>
          Meanwhile, District 8 families: especially those with special needs
          students: continue to face inadequate services, overcrowded
          classrooms, and declining accountability.
        </BodyText>
        <BodyText>
          <strong>
            My position is one of accountability. Everyone who had financial
            oversight responsibilities of the school district finances should
            have been terminated. In the private sector, everyone would have
            been fired and mismanagement of taxpayer money should not be treated
            any differently. It is the only way to build trust back that our tax
            dollars are being accounted for and monitored appropriately.
          </strong>
        </BodyText>
        <BulletList
          items={[
            "Parental Rights: Parents deserve a voice in their children's education. That voice includes curriculum transparency and religious freedom options.",
            "Teachers must be supported with necessary resources and without having to pay out of their pocket.",
            "Both teachers and students need to be protected from disruptive or violent students. Teachers need to be able to focus on being teachers and not having to wear a variety of hats.",
          ]}
        />
        <CtaLink sectionId="petition">
          Sign the Petition Against No-Vote Tax Increases →
        </CtaLink>
      </>
    ),
  },
  {
    id: "tax",
    icon: "💲",
    title: "Tax Fairness & Cost of Living",
    subtitle:
      "Before raising taxes, show taxpayers every dollar is being spent responsibly.",
    content: (
      <>
        <SectionHeading>The Problem</SectionHeading>
        <BodyText>
          Delaware families are struggling with rising costs everywhere:
          housing, groceries, utilities, healthcare. Meanwhile, government
          spending continues to grow without accountability.
        </BodyText>
        <BulletList
          items={[
            <span key="fi">
              <strong>Fiscal Responsibility:</strong> Government should live
              within its means, just like families do. Every tax dollar must be
              spent wisely and transparently. State spending is outpacing actual
              state income. All government spending programs should be audited
              and verified that the results are supporting the continued
              funding. Cuts should be made when results don't support the
              funding and cuts should be on the table when operating in a
              deficit. The state cannot fund every program out there, regardless
              of how well intended they are.{" "}
              <strong>STOP THE TAX AND SPEND</strong>
            </span>,
            <span key="pfi">
              <strong>Protect Fixed Incomes:</strong> Seniors and families on
              fixed incomes deserve protections from sudden, or significant,
              property and school tax increases that threaten housing security.
            </span>,
            "Vote against unnecessary tax increases",
            "Demand transparency in government spending",
            "Support property tax relief for seniors and fixed-income residents",
            "Advocate for regulatory reforms that lower costs for families and businesses",
          ]}
        />
        <CtaLink sectionId="volunteer">Join the Campaign →</CtaLink>
      </>
    ),
  },
  {
    id: "smallbiz",
    icon: "🏢",
    title: "Supporting Small Businesses",
    subtitle:
      "Small businesses are vital to Delaware and especially to Middletown.",
    content: (
      <>
        <SectionHeading>The Problem</SectionHeading>
        <BodyText>
          Small businesses drive Middletown's economy: they create jobs, serve
          our community, and give our town its character. But excessive
          regulations, rising costs, and bureaucratic red tape make it harder
          every year for small business owners to survive and thrive.
        </BodyText>
        <BulletList
          items={[
            <span key="rrt">
              <strong>Reduce Red Tape:</strong> Streamline regulations that
              create barriers without improving safety or quality.
            </span>,
            <span key="loc">
              <strong>Lower Operating Costs:</strong> Oppose unnecessary fees,
              taxes, and mandates that hurt small businesses.
            </span>,
            <span key="lf">
              <strong>Listen First:</strong> Before voting on business-related
              legislation, consult actual business owners about real-world
              impact.
            </span>,
            <span key="sl">
              <strong>Support Local:</strong> Prioritize policies that help
              Middletown businesses compete and grow.
            </span>,
            "Oppose excessive regulations that burden small businesses",
            "Support tax relief and incentives for local businesses",
            "Advocate for faster permitting and licensing processes",
            "Work with the business community to identify real barriers to growth",
          ]}
        />
      </>
    ),
  },
  {
    id: "safety",
    icon: "🛡️",
    title: "Public Safety & Criminal Justice",
    subtitle:
      "30 years in law enforcement. I have seen firsthand what accountability in public safety looks like.",
    content: (
      <>
        <SectionHeading>Gary's Experience</SectionHeading>
        <BodyText>
          I spent 20 years protecting this community as a law enforcement
          officer. I know what works and what doesn't when it comes to keeping
          neighborhoods safe. Public safety isn't about slogans or politics.
          It's about results. It's about actually addressing the criminals and
          holding them accountable.
        </BodyText>
        <StatBlock number="20+ Years" label="Active law enforcement service" />
        <BulletList
          items={[
            <span key="am">
              <strong>Accountability Matters:</strong> Hold criminals
              accountable.
            </span>,
            <span key="cp">
              <strong>Community Policing:</strong> Support strong relationships
              between law enforcement and the community to prevent, or reduce,
              crime and build trust.
            </span>,
            <span key="vr">
              <strong>Victim Rights:</strong> The criminal justice system must
              prioritize victims over offenders. Hard stop.
            </span>,
            "Oppose policies that reduce accountability for criminals.",
            "Support funding for law enforcement training and resources.",
            "Advocate for strong victim rights.",
            "Work to keep dangerous offenders off our streets",
          ]}
        />
      </>
    ),
  },
  {
    id: "seniors",
    icon: "💙",
    title: "Supporting Seniors & Fixed Incomes",
    subtitle:
      "We must explore safeguards that protect residents on fixed incomes from housing insecurity.",
    content: (
      <>
        <SectionHeading>The Problem</SectionHeading>
        <BodyText>
          Seniors and families on fixed incomes are being priced out of their
          homes by rising property and school taxes, utility costs, and
          healthcare expenses. These sudden increases hit all of us, but
          especially this group hard. Those on Social Security or pensions can't
          afford sudden, or large, increases.
        </BodyText>
        <BulletList
          items={[
            <span key="ptp">
              <strong>Property Tax Protections:</strong> Cap increases for
              seniors and those on fixed incomes to prevent displacement.
            </span>,
            <span key="ua">
              <strong>Utility Assistance:</strong> Expand programs that help
              fixed-income residents with rising utility costs.
            </span>,
            <span key="aip">
              <strong>Aging in Place:</strong> Support policies that let seniors
              stay in their homes and communities.
            </span>,
          ]}
        />
      </>
    ),
  },
  {
    id: "utility",
    icon: "⚡",
    title: "Utility Costs",
    subtitle:
      "We must identify the causes of recent utility rate increases and address them honestly.",
    content: (
      <>
        <SectionHeading>The Problem</SectionHeading>
        <BodyText>
          Delaware families have seen significant increases in utility bills
          recently. While some increases are unavoidable, ratepayers deserve
          transparency about why costs are rising and accountability for how
          utility companies operate.
        </BodyText>
        <BulletList
          items={[
            "Demand transparency from utility companies. They must clearly explain rate increases and justify the costs. Transparent billing on the charges to the customer.",
            <span key="ea">
              <strong>Energy Alternatives:</strong> Support policies that
              increase energy choice and competition where feasible.
            </span>,
            <span key="ap">
              <strong>Assistance Programs:</strong> Review support for low
              and/or fixed income families struggling with utility bills.
            </span>,
          ]}
        />
      </>
    ),
  },
];

// ── Single card component ─────────────────────────────────────────────────────

const IssueCard = ({ issue, isOpen, onToggle }) => (
  <Box
    sx={{
      background: "#fff",
      borderRadius: "6px",
      border: "1px solid",
      borderColor: isOpen ? "#c9962a" : "#e8e2d4",
      overflow: "hidden",
      mb: "12px",
      boxShadow: isOpen ? "0 4px 20px rgba(201,150,42,0.12)" : "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: "#c9962a",
        boxShadow: "0 4px 20px rgba(201,150,42,0.08)",
      },
    }}
  >
    {/* Header */}
    <Box
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "18px 24px",
        cursor: "pointer",
        userSelect: "none",
        transition: "background 0.2s",
        "&:hover": { background: "rgba(201,150,42,0.02)" },
      }}
    >
      {/* Emoji icon box */}
      <Box
        aria-hidden="true"
        sx={{
          width: "38px",
          height: "38px",
          borderRadius: "6px",
          background: "#faf3e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "17px",
          flexShrink: 0,
        }}
      >
        {issue.icon}
      </Box>

      {/* Title + subtitle */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "15px",
            color: "#1a1a2e",
            lineHeight: 1.3,
          }}
        >
          {issue.title}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#6b6b80",
            mt: "3px",
            lineHeight: 1.4,
          }}
        >
          {issue.subtitle}
        </Typography>
      </Box>

      {/* Chevron — › when closed, ˅ when open */}
      <Typography
        aria-hidden="true"
        sx={{
          fontSize: "20px",
          color: isOpen ? "#c9962a" : "#6b6b80",
          flexShrink: 0,
          lineHeight: 1,
          fontWeight: 300,
          transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
        }}
      >
        ›
      </Typography>
    </Box>

    {/* Expandable content */}
    <Box
      sx={{
        maxHeight: isOpen ? "2000px" : "0px",
        overflow: "hidden",
        transition: isOpen
          ? "max-height 0.5s ease-in"
          : "max-height 0.35s ease-out",
      }}
    >
      <Box sx={{ borderTop: "1px solid #e8e2d4", padding: "0 24px 28px" }}>
        {issue.content}
      </Box>
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const Issues = () => {
  // Multiple cards can be open at the same time (matches Netlify behaviour)
  const [openIds, setOpenIds] = useState(new Set());

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Box
      id="issues"
      component="section"
      sx={{ background: "#ffffff", padding: "80px 0" }}
    >
      <Container maxWidth="lg">
        {/* Section label */}
        <Typography
          sx={{
            color: "#C59324",
            fontWeight: 700,
            letterSpacing: "0.2em",
            fontSize: "0.85rem",
            textTransform: "uppercase",
            mb: 2,
          }}
        >
          The Issues
        </Typography>

        {/* Section heading */}
        <Box sx={{ maxWidth: "640px", mb: "32px" }}>
          <Typography
            variant="h2"
            sx={{
              mb: "8px",
              color: "#333",
              fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              mt: 1,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
              },
            }}
          >
            How I Will{" "}
            <Box
              component="em"
              sx={{
                color: "#c9962a",
                fontStyle: "italic",
                fontWeight: 500,
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Vote on Legislation
            </Box>
          </Typography>
          <Typography
            sx={{ fontSize: "16px", color: "#6b6b80", lineHeight: 1.7 }}
          >
            I will vote yes or no based on merits: does this make sense for
            Middletown? Not based on party labels or political pressure.
          </Typography>
        </Box>

        {/* Cards */}
        {ISSUES.map((issue) => (
          <IssueCard
            key={issue.id}
            issue={issue}
            isOpen={openIds.has(issue.id)}
            onToggle={() => toggle(issue.id)}
          />
        ))}
      </Container>
    </Box>
  );
};

export default Issues;
